import type { ProductModifierValue } from "schemas/generated/fileModifiers";
import type { Product } from "schemas/generated/product";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useProductList } from "@editor/hooks/useProductList";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import startCase from "lodash-es/startCase";
import { ChevronDown, ImageIcon } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useSelectedElement } from "../store/ModifiersStoreContext";

interface ProductSelectorModifierProps {
  onChange: (modifiers: ProductModifierValue[]) => void;
}

const ProductSelectorModifier: React.FC<ProductSelectorModifierProps> = ({
  onChange,
}) => {
  const selectedElement = useSelectedElement();
  const projectId = useCurrentProjectId();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const [currentProductId, setCurrentProductId] = useOverridableState(
    selectedElement?.props?.productId,
    React.useCallback(
      (productId: string) => {
        const modifiers: ProductModifierValue[] = [
          { type: "product", value: productId },
        ];
        onChange(modifiers);
      },
      [onChange],
    ),
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const { products } = useProductList({
    projectId,
    limit: 20,
  });

  const currentProduct = products.find(
    (product) => product.id === currentProductId,
  );

  const handleProductSelect = (product: Product) => {
    setCurrentProductId(product.id);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current Selection Display */}
      <div
        className="flex items-center justify-between p-2 border border-border rounded-md cursor-pointer hover:bg-surface"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {currentProduct ? (
            <>
              {currentProduct.featuredMedia?.url ? (
                <div className="bg-surface h-6 w-6 flex-shrink-0 flex items-center justify-center rounded overflow-hidden">
                  <img
                    src={currentProduct.featuredMedia.url}
                    alt={currentProduct.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="bg-surface h-6 w-6 flex-shrink-0 flex items-center justify-center rounded">
                  <ImageIcon className="text-muted size-3" />
                </div>
              )}
              <span className="text-sm font-medium truncate">
                {currentProduct.title}
              </span>
            </>
          ) : (
            <span className="text-sm text-muted truncate min-w-0">
              Select a product...
            </span>
          )}
        </div>
        <ChevronDown className="size-4 text-muted" />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
          {products.length === 0 ? (
            <div className="p-3 text-sm text-muted text-center">
              No products found
            </div>
          ) : (
            products.map((product: Product) => {
              const tagVariant: "success" | "info" | "neutral" =
                exhaustiveSwitch(
                  product,
                  "status",
                )({
                  active: "success",
                  draft: "info",
                  archived: "neutral",
                });

              return (
                <div
                  key={product.id}
                  className={twMerge(
                    "flex flex-row gap-2 p-2 items-center cursor-pointer hover:bg-surface",
                    currentProductId === product.id && "bg-menu-item-selected",
                  )}
                  onClick={() => handleProductSelect(product)}
                >
                  {product.featuredMedia?.url ? (
                    <div className="bg-surface h-6 w-6 flex-shrink-0 flex items-center justify-center rounded overflow-hidden">
                      <img
                        src={product.featuredMedia.url}
                        alt={product.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="bg-surface h-6 w-6 flex-shrink-0 flex items-center justify-center rounded">
                      <ImageIcon className="text-muted size-3" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate min-w-0">
                      {product.title}
                    </div>
                    <div className="flex items-center gap-1">
                      <Tag size="sm" variant={tagVariant}>
                        {startCase(product.status)}
                      </Tag>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSelectorModifier;
